import React, { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";

/* ───────────── §7 tokens ───────────── */
export const T = {
  ground: "#F6F6F8", card: "#FFFFFF", rail: "#FBFBFD", hair: "#E9E9EC", soft: "#F1F1F4",
  ink: "#141412", sub: "#6C6C69", faint: "#6E6E6B", mute: "#9B9B97",
  indigo: "#5856D6", amberDot: "#FF9500", amberTxt: "#9A5A00", red: "#C4302A", green: "#34C759",
  shadow: "0 10px 28px rgba(20,20,18,.14)",
};

/* ───────────── Lens mark: square corners, upload arrow, ink, thin stroke ───────────── */
// The Lens mark — lucide "ScanLine" scan-frame (corner brackets + centre line), used for every upload affordance.
export function LensMark({ size = 13, color = T.ink }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
      <path d="M7 12h10" />
    </svg>
  );
}

export function Dot({ kind, size = 7 }) {
  const bg = kind === "ok" ? T.green : kind === "ai" ? T.indigo : kind === "amber" ? T.amberDot : kind === "red" ? T.red : T.mute;
  return <span style={{ width: size, height: size, borderRadius: 999, background: bg, flexShrink: 0, display: "inline-block" }} />;
}

/* ───────────── Create-Claims-style bordered pulldown (native select in a .tsel pill) ───────────── */
export function Pulldown({ label, value, onChange, options, ariaLabel, testid, width }) {
  return (
    <label className="tsel" style={width ? { width } : undefined} data-testid={testid}>
      {label ? <span>{label}</span> : null}
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={ariaLabel} style={{ flex: width ? 1 : undefined }}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
      </select>
    </label>
  );
}

/* ───────────── Segmented control (.seg, ported from mock) ───────────── */
export function Segmented({ value, onChange, segments }) {
  return (
    <div className="seg" role="tablist" data-testid="pd-segments">
      {segments.map((s) => {
        const on = value === s.v;
        return (
          <button key={s.v} type="button" role="tab" aria-selected={on} data-testid={`pd-seg-${s.v}`}
            className={on ? "on" : ""} onClick={() => onChange(s.v)}>
            {s.dot ? <Dot kind={s.dot} /> : null}{s.t} <span className="n tabular-nums">{s.n}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ───────────── Sort & Filter popover ───────────── */
export function SortFilterPopover({ sort, onSort, typeCounts, typeFilter, onTypeFilter, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const active = typeFilter.size;
  const toggleType = (t) => {
    const next = new Set(typeFilter);
    next.has(t) ? next.delete(t) : next.add(t);
    onTypeFilter(next);
  };
  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid={testid} onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-[10px] px-2.5 h-[32px] text-[13.5px] font-semibold" style={{ border: `1px solid ${T.hair}`, background: T.card, color: T.ink }}>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={T.ink} strokeWidth="1.6" strokeLinecap="round"><path d="M2 4h12M4 8h8M6 12h4" /></svg>
        {onSort ? "Sort \u0026 Filter" : "Filter"}{active ? ` · ${active}` : ""}
      </button>
      {open ? (
        <div className="absolute right-0 top-[110%] z-50 p-3 rounded-[14px] w-[260px]" style={{ background: T.card, border: `1px solid ${T.hair}`, boxShadow: T.shadow }} data-testid="pd-sortfilter-panel">
          {onSort ? (<>
          <div className="text-[10.5px] font-bold uppercase tracking-[.04em] mb-1.5" style={{ color: T.mute }}>Sort by</div>
          {[["dos", "Date of service"], ["uploaded", "Uploaded, newest first"], ["name", "Document name"]].map(([v, t]) => (
            <button key={v} type="button" data-testid={`pd-sort-${v}`} onClick={() => onSort(v)}
              className="w-full flex items-center justify-between px-2 py-1.5 rounded-[8px] text-[13px] hover:bg-black/[.04]" style={{ color: T.ink }}>
              {t}{sort === v ? <Check size={14} style={{ color: T.indigo }} /> : null}
            </button>
          ))}
          <div className="h-px my-2" style={{ background: T.hair }} />
          </>) : null}
          <div className="text-[10.5px] font-bold uppercase tracking-[.04em] mb-1.5" style={{ color: T.mute }}>Document type</div>
          {Object.keys(typeCounts).length === 0 ? <div className="px-2 py-1 text-[12px]" style={{ color: T.mute }}>No documents</div> : null}
          {Object.entries(typeCounts).map(([t, n]) => (
            <button key={t} type="button" data-testid={`pd-type-${t}`} onClick={() => toggleType(t)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-[8px] text-[13px] hover:bg-black/[.04]" style={{ color: T.ink }}>
              <span className="w-[15px] h-[15px] rounded-[4px] flex items-center justify-center" style={{ border: `1.5px solid ${typeFilter.has(t) ? T.indigo : T.mute}`, background: typeFilter.has(t) ? T.indigo : "transparent" }}>
                {typeFilter.has(t) ? <Check size={11} strokeWidth={3} color="#fff" /> : null}
              </span>
              <span className="flex-1 text-left">{prettyType(t)}</span>
              <span className="tabular-nums" style={{ color: T.mute }}>{n}</span>
            </button>
          ))}
          {active ? <button type="button" data-testid="pd-type-clear" onClick={() => onTypeFilter(new Set())} className="mt-1 text-[12px] font-semibold" style={{ color: T.indigo }}>Clear types</button> : null}
        </div>
      ) : null}
    </div>
  );
}

/* ───────────── formatters ───────────── */
export const TYPE_LABELS = {
  encounter_note: "Encounter note", id_card: "Insurance card", government_id: "Government ID",
  lab_result: "Lab result", radiology_report: "Radiology report",
};
export const titleCase = (s) => String(s || "").replace(/\b\w/g, (c) => c.toUpperCase());
// Patient-name display: normalize all-caps / all-lowercase OCR names to Title Case
// (SERGIO SAMANO → Sergio Samano) but leave genuinely mixed-case names alone (McDonald, DeLuca).
export const displayName = (s) => {
  const str = String(s || "").trim();
  if (!str) return "";
  if (/[a-z]/.test(str) && /[a-z][A-Z]/.test(str)) return str;
  return str.toLowerCase().replace(/\b\p{L}/gu, (c) => c.toUpperCase());
};
export const sentenceCase = (k) => { const w = String(k || "").replace(/_/g, " ").trim(); return w ? w[0].toUpperCase() + w.slice(1) : w; };
export const snakeWords = (v) => { const s = String(v ?? ""); return /^[a-z0-9]+(_[a-z0-9]+)+$/.test(s) ? titleCase(s.replace(/_/g, " ")) : s; };
export const prettyType = (t) => TYPE_LABELS[t] || titleCase(String(t || "document").replace(/_/g, " "));

function toDate(v) {
  if (!v) return null;
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return new Date(+m[3], +m[1] - 1, +m[2]);
  const d = new Date(s);
  return isNaN(d) ? null : d;
}
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n) => String(n).padStart(2, "0");
export function fmtMDY(v) { const d = toDate(v); return d ? `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}` : ""; }
export function fmtHuman(v) { const d = toDate(v); return d ? `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}` : ""; }
export function ageFromDob(v) { const d = toDate(v); if (!d) return null; const t = new Date(); let a = t.getFullYear() - d.getFullYear(); const m = t.getMonth() - d.getMonth(); if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a--; return a >= 0 && a < 130 ? a : null; }

// Uploaded column from created_at (UTC) → local; today → time, else "Mon D".
export function fmtUploaded(iso, justNow) {
  if (justNow) return "Just now";
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d)) return "—";
  const now = new Date();
  const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  if (sameDay) return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}
export function fmtUploadedLong(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d)) return "—";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// first present of several keys
export function roleValue(ex, keys) {
  if (!ex) return "";
  for (const k of keys) { const v = ex[k]; if (v != null && v !== "" && !(Array.isArray(v) && !v.length)) return v; }
  return "";
}
const DATE_KEYS = ["encounter_date", "date_of_service", "visit_date", "exam_date", "study_date", "collected_at", "collection_date", "resulted_at", "service_date", "document_date"];
export function docDate(ex) { return fmtMDY(roleValue(ex, DATE_KEYS)); }

const LAB_FLAG_CRIT = new Set(["hh", "ll", "critical", "panic"]);
const LAB_FLAG_ANY = new Set(["h", "l", "hh", "ll", "critical", "panic", "a", "abnormal"]);
export function labFlagKind(flag) {
  const f = String(flag || "").toLowerCase();
  if (LAB_FLAG_CRIT.has(f)) return "crit";
  if (LAB_FLAG_ANY.has(f)) return "flag";
  return "";
}

// Detail column by type → { text, amber }
export function detailLine(docType, ex) {
  if (!ex) return { text: "", amber: false };
  if (docType === "lab_result") {
    const name = roleValue(ex, ["test_name", "panel_name", "test", "name"]);
    const results = Array.isArray(ex.results) ? ex.results : [];
    const quals = results.filter((r) => r && r.value && isNaN(Number(String(r.value).replace(/[^0-9.]/g, "")))).map((r) => r.value);
    const flagged = results.filter((r) => r && labFlagKind(r.flag));
    if (flagged.length) return { text: `${name} · ${flagged.length} outside range`, amber: true };
    if (quals.length) return { text: `${name} · ${quals.slice(0, 3).join(", ")}`, amber: false };
    if (results.length) return { text: `${name} · all within range`, amber: false };
    return { text: name, amber: false };
  }
  if (docType === "radiology_report") {
    const b = roleValue(ex, ["birads", "bi_rads", "bi_rads_score"]);
    const exam = roleValue(ex, ["exam", "exam_name", "study", "study_name"]);
    const amber = ["0", "4", "5"].includes(String(b));
    return { text: b !== "" ? `BI-RADS ${b} · ${exam}` : exam, amber };
  }
  if (docType === "encounter_note") {
    const t = roleValue(ex, ["encounter_type", "visit_type"]);
    const p = roleValue(ex, ["provider_name", "rendering_provider", "ordering_provider", "signed_by"]);
    return { text: [t, p].filter(Boolean).join(" · "), amber: false };
  }
  if (docType === "id_card") {
    const payer = roleValue(ex, ["payer_name", "payer", "insurance"]);
    const plan = roleValue(ex, ["plan_name"]);
    return { text: [payer, plan].filter(Boolean).join(" · "), amber: false };
  }
  if (docType === "medication_summary" || docType === "medication_list") {
    const meds = Array.isArray(ex.medications) ? ex.medications : [];
    const act = meds.filter((m) => m.status !== "discontinued").length, al = Array.isArray(ex.allergies) ? ex.allergies.length : 0;
    return { text: `${act} active medication${act === 1 ? "" : "s"}${al ? ` · ${al} allerg${al === 1 ? "y" : "ies"}` : ""}`, amber: false };
  }
  if (docType === "government_id") {
    const idt = snakeWords(roleValue(ex, ["gov_id_type", "id_type"]));
    const auth = roleValue(ex, ["gov_issuing_authority", "gov_state", "issuing_authority"]);
    return { text: [idt, auth].filter(Boolean).join(" · "), amber: false };
  }
  return { text: "", amber: false };
}

// Patient name + dob from extraction for the list Patient column.
// Prefer first/middle/last so names read "Tamika S Foat", not "Foat Tamika S".
export function patientBits(ex) {
  const first = roleValue(ex, ["first_name", "patient_first_name", "gov_first_name"]);
  const middle = roleValue(ex, ["middle_name", "patient_middle_name", "gov_middle_name"]);
  const last = roleValue(ex, ["last_name", "patient_last_name", "gov_last_name"]);
  const raw = (first || last)
    ? [first, middle, last].filter(Boolean).join(" ")
    : roleValue(ex, ["patient_name", "patient_full_name", "full_name", "gov_full_name", "name"]);
  const dob = fmtMDY(roleValue(ex, ["date_of_birth", "dob", "birth_date", "patient_dob", "gov_date_of_birth"]));
  return { name: displayName(raw), rawName: String(raw || ""), dob };
}

/* ── Thin-row aware accessors (Delta-Sync) ──────────────────────────────────
   /analyses/thin carries backend-resolved display fields instead of the heavy
   `extraction` blob. These prefer those fields and fall back to `extraction`,
   so the detail (heavy) shape keeps rendering unchanged. */
export function rowPatientBits(row) {
  if (row && !row.extraction && (row.read_patient_name != null || row.read_patient_dob != null)) {
    return { name: displayName(row.read_patient_name || ""), rawName: String(row.read_patient_name || ""), dob: fmtMDY(row.read_patient_dob) };
  }
  return patientBits(row?.extraction);
}
export function rowDate(row) {
  if (row && !row.extraction && row.service_date != null) return fmtMDY(row.service_date);
  return docDate(row?.extraction);
}
export function rowSender(row) {
  if (row && !row.extraction) return row.sender || "";
  return roleValue(row.extraction, ["sender", "from", "sender_name"]);
}
export function rowSubject(row) {
  if (row && !row.extraction) return row.subject || "";
  return roleValue(row.extraction, ["subject", "title", "document_title"]);
}

/* ───────────── status wording + dot per §2 ───────────── */
export function rowStatus(row) {
  const link = row.edi_link_status;
  const m = row.edi_client_match || {};
  const np = row.edi_new_patient || {};
  const patient = m.patient?.full_name ? displayName(m.patient.full_name) : "";
  if (link === "confirmed") return { text: `Confirmed · ${patient || "patient"}`, dot: "ok", amber: false };
  if (link === "auto_linked") return { text: `Auto-linked · ${patient || "patient"}`, dot: "ai", amber: false };
  if (link === "new_patient") return { text: `New patient · ${patient || np.mrn || "created"}`, dot: "ai", amber: false };
  // needs_review
  if (m.status === "tentative") return { text: "Possible match", dot: "amber", amber: true };
  return { text: "No chart match", dot: "amber", amber: true };
}

/* ───────────── Relevance (new column) ───────────── */
export const RELEVANCE_LABELS = {
  patient_specific: "Patient-specific", population_specific: "Population",
  clinic_communication: "Clinic comms", non_healthcare: "Non-healthcare", unclassified: "Unclassified",
};
export const prettyRelevance = (r) => RELEVANCE_LABELS[r] || (r ? titleCase(String(r).replace(/_/g, " ")) : "—");

/* ───────────── Chart-data (ingest) status → dot + wording ───────────── */
export function chartStatusInfo(row) {
  const s = row.chart_data_status || row.edi_chart_status;
  const linked = ["auto_linked", "confirmed", "new_patient"].includes(row.edi_link_status) || !!row.linked_chart_id;
  // Only patient-specific documents belong on a patient chart. Anything else (population,
  // clinic comms, non-healthcare, unclassified) never shows an "Awaiting patient" prompt.
  const patientDoc = (row.edi_relevance || "patient_specific") === "patient_specific";
  const awaiting = patientDoc
    ? { key: "awaiting", text: "Awaiting patient", dot: "amber", amber: true }
    : { key: "na", text: "Not for a chart", dot: "mute" };
  switch (s) {
    case "ingested": return { key: "ingested", text: "Added", dot: "ok" };
    case "ingesting": return { key: "ingesting", text: "Adding…", dot: "ai", busy: true };
    case "ready": return { key: "ready", text: "Ready to add", dot: "ai" };
    case "awaiting": return awaiting;
    case "failed": return { key: "failed", text: "Couldn't add", dot: "red", amber: true };
    case "removed": return { key: "removed", text: "Removed", dot: "mute" };
    case "nothing": return { key: "nothing", text: "Nothing new", dot: "mute" };
    default:
      if (!linked) return awaiting;
      return { key: "ready", text: "Ready to add", dot: "ai" };
  }
}

/* ───────────── compact link label + match confidence (for the 2-line Status cell) ───────────── */
export const LINK_SHORT = { auto_linked: "Auto-linked", confirmed: "Confirmed", new_patient: "New patient", needs_review: "Needs review" };
export function linkShort(row) {
  const s = rowStatus(row);
  return { text: LINK_SHORT[row.edi_link_status] || s.text, dot: s.dot, amber: s.amber };
}
export function matchConfidence(row) {
  const c = row.edi_client_match?.confidence;
  return c == null ? null : Math.round(c * 100);
}

/* ───────────── clinical detail + source + patient identity (for the grouped layout) ───────────── */
export function sourceOf(row) {
  const e = row.extraction || {};
  return roleValue(e, ["provider_name", "signed_by", "facility_name", "performing_lab", "ordering_provider", "pharmacy_name", "referring_provider", "imaging_facility"]) || "";
}
export function descOf(row) {
  const s = row.edi_chart_summary;
  if (typeof s === "string" && s.trim()) return s;
  if (!row.extraction && typeof row.summary_line === "string" && row.summary_line.trim()) return row.summary_line;
  // edi_chart_summary is sometimes an ingest-result object { added, skipped, ... } — that belongs
  // to chart status, not the clinical description, so fall through to the extracted clinical detail.
  const d = detailLine(row.document_type, row.extraction);
  if (d.text) return d.text;
  const e = row.extraction || {};
  return roleValue(e, ["encounter_type", "chief_complaint", "assessment", "document_title"]) || "";
}
export function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}
// Who/which chart a document belongs to — drives patient grouping and the "No chart yet" state.
export function patientOf(row) {
  const m = row.edi_client_match || {};
  const p = m.patient || {};
  const np = row.edi_new_patient || {};
  const pb = row.extraction ? patientBits(row.extraction) : { name: displayName(row.read_patient_name || ""), rawName: String(row.read_patient_name || ""), dob: fmtMDY(row.read_patient_dob) };
  const name = (p.full_name ? displayName(p.full_name) : "") || (np.full_name ? displayName(np.full_name) : "") || pb.name || "";
  const rawName = (p.full_name || np.full_name || pb.rawName || "");
  const dob = fmtMDY(p.dob || np.dob) || pb.dob || "";
  const mrn = p.mrn || np.mrn || "";
  const chartUuid = row.linked_chart_uuid || p.chart_uuid || "";
  // Auto-linked / confirmed means Lens matched the doc to an EXISTING chart — that patient has a chart.
  // Only a brand-new patient (new_patient) or an unmatched doc should surface the "Create chart" prompt.
  const hasChart = ["auto_linked", "confirmed"].includes(row.edi_link_status) || !!row.linked_chart_id || (!!chartUuid && p.has_medical_data);
  return { name, rawName, dob, mrn, chartUuid, hasChart };
}
export function groupKeyOf(row) {
  const p = patientOf(row);
  return p.chartUuid || p.mrn || (p.name && p.dob ? `${p.name}|${p.dob}` : p.name) || "__unmatched";
}



/* ───────────── JSON-aware value rendering (no more raw JSON dumps) ───────────── */
// Parse a value that may be an object/array OR a JSON-looking string into real data.
export function coerceJson(v) {
  if (v == null) return v;
  if (typeof v === "object") return v;
  if (typeof v === "string") {
    const s = v.trim();
    if ((s.startsWith("{") && s.endsWith("}")) || (s.startsWith("[") && s.endsWith("]"))) {
      try { return JSON.parse(s); } catch { return v; }
    }
  }
  return v;
}
// True when the value (after coercion) is an object/array worth structured rendering.
export function isComplexValue(v) {
  const c = coerceJson(v);
  return c !== null && typeof c === "object";
}

const scalarStr = (x) => (x == null || x === "" ? "" : (typeof x === "object" ? JSON.stringify(x) : snakeWords(x)));
const kvInline = (obj) => Object.entries(obj).filter(([, x]) => x != null && x !== "").map(([k, x]) => `${sentenceCase(k)}: ${scalarStr(x)}`).join("  ·  ");

// Renders any extracted value in a human-readable way (bounded depth, no recursion):
// scalars as text, arrays of objects as stacked mini-cards, arrays of scalars as chips,
// objects as a key/value card.
export function PrettyValue({ value }) {
  const v = coerceJson(value);
  if (v == null || v === "") return <span style={{ color: T.mute }}>—</span>;
  if (Array.isArray(v)) {
    if (!v.length) return <span style={{ color: T.mute }}>—</span>;
    const objs = v.some((x) => x && typeof x === "object");
    if (objs) {
      return (
        <div className="space-y-1.5">
          {v.map((x, i) => (
            <div key={i} className="rounded-[8px] px-2.5 py-1.5 text-[12px]" style={{ border: `1px solid ${T.hair}`, background: T.rail, color: T.ink }}>
              {x && typeof x === "object" ? kvInline(x) : scalarStr(x)}
            </div>
          ))}
        </div>
      );
    }
    return <div className="flex flex-wrap gap-1.5">{v.map((x, i) => <span key={i} className="inline-flex text-[11.5px] px-2 py-[2px] rounded-[6px]" style={{ background: T.soft, color: T.sub }}>{scalarStr(x)}</span>)}</div>;
  }
  if (typeof v === "object") {
    const entries = Object.entries(v).filter(([, x]) => x != null && x !== "" && !(Array.isArray(x) && !x.length));
    if (!entries.length) return <span style={{ color: T.mute }}>—</span>;
    return (
      <div className="rounded-[8px] px-2.5 py-1.5" style={{ border: `1px solid ${T.hair}`, background: T.rail }}>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {entries.map(([k, x]) => (
            <span key={k} className="inline-flex items-baseline gap-1 text-[12px]">
              <span style={{ color: T.mute }}>{sentenceCase(k)}:</span>
              <span className="font-semibold" style={{ color: T.ink }}>{x && typeof x === "object" ? kvInline(x) : scalarStr(x)}</span>
            </span>
          ))}
        </div>
      </div>
    );
  }
  return <span style={{ color: T.ink }}>{scalarStr(v)}</span>;
}
